import { StrictMode, useEffect } from 'react';
import { TranslationNamespaces } from '~/lib/constants';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './app.css';

import { ErrorBoundary, AppComponent, Component as Root } from '~/routes/layouts/root';
import { RoutingConstants } from './apps/smartsend/lib/consts/RoutingConstants';

// SmartSend / MyAccount
import MyAccount from './routes/layouts/my-account/my-account';
import SmartSendLayout from './routes/layouts/my-account/smartsend-layout';
import ConsumerLayout from './routes/layouts/my-account/consumer-layout';
import SettingsLayout from './routes/layouts/my-account/account';
import OrdersLayout from './routes/layouts/my-account/orders';
import SupportLayout from './routes/layouts/my-account/support';
import ToolsLayout from './routes/layouts/my-account/tools';
import RewardsLayout from './routes/layouts/my-account/rewards';
import SmartSendDashboard from '~/routes/analytics/dashboard';
import SmartSendOnboarding from '~/routes/smartsend/setup';

// My Account
import AbortSetup from '~/routes/smartsend/abort-setup';
import PickAndPacking from '~/routes/smartsend/pick-and-packing';
import MarketplaceTracking from '~/routes/smartsend/marketplace-tracking';
import RuleManager from '~/routes/rulemanager/rulemanager';
import Manifests from '~/routes/smartsend/manifests';
import CustomerFeatures from '~/routes/customer-features/customer-features';

// Mini Apps
import PickingApp from '~/routes/mini-apps/picking-app';
import PickingList from '~/routes/mini-apps/picking-list';
import TariffLookup from '~/routes/mini-apps/tariff-codes';

// Other
import SignIn from '~/routes/signin';
import Silent from '~/routes/silent';
import { childrenOfOrders } from './routes/groups/orders';
import { childrenOfSupport } from './routes/groups/support';
import { childrenOfAccount } from './routes/groups/account';
import { childrenOfTools } from './routes/groups/tools';
import { consumerRoutes } from './routes/groups/consumer';
import { legacyRoutes } from './routes/groups/legacy';
import LegacyRoute from './routes/legacy-route';
import { childrenOfRewards } from './routes/groups/rewards';

import { sentryDenyUrls, sentryIgnoreErrors } from '~/lib/sentry-filters';
import { scrubBreadcrumb, scrubEventUser } from '~/lib/sentry-pii';
import { installChunkLoadRecovery } from '~/lib/chunk-load-recovery';
import { createSentryBeforeSend } from '~/lib/sentry-before-send';

// Reload once when a lazy chunk from a previous deploy fails to load (UI-MYACCOUNT-1P, 5Y, 50, BE).
const chunkLoadRecovery = installChunkLoadRecovery();

Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || 'Debug',
  release: import.meta.env.VITE_VERSION || '0.0.0',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        window.location.origin,
        'https://smartsend-edge.serverless.p2g.systems',
        'https://me.serverless.p2g.systems'
      ],
      networkResponseHeaders: [
        'x-tenant',
        'x-region',
        'x-trace',
        'x-cache',
        'cf-cache-status',
        'x-amzn-trace-id',
        'cache-control'
      ],
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]']
    }),
    Sentry.feedbackIntegration({
      autoInject: false
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['parcel2go-uiz'],
      behaviour: 'drop-error-if-contains-third-party-frames'
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  ignoreErrors: sentryIgnoreErrors,
  denyUrls: sentryDenyUrls,
  // Third-party / SDK noise, the recovered chunk-load error, and the user scrub. See ~/lib/sentry-before-send.
  beforeSend: createSentryBeforeSend(chunkLoadRecovery),
  // Postcodes / address searches in fetch and navigation URLs, and raw name/email in analytics and
  // console breadcrumbs. See ~/lib/sentry-pii.
  beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  // Performance Monitoring. The comment here used to claim 100%; the rate has been 1% for some time.
  tracesSampleRate: 0.01,
  // Session Replay.
  //
  // Session sampling was 0.1. Across the org that put replay demand at ~4x a 25k/month reserved
  // quota shared with seven other projects, and it ran out on day 20 of the billing period; this
  // app was ~29% of that. replaysOnErrorSampleRate deliberately stays at 1.0 - a replay for a
  // session that actually errored is the one worth having.
  replaysSessionSampleRate: 0.02,
  replaysOnErrorSampleRate: 1.0
});

// `beforeSend` only sees error events. Transactions and Session Replay events carry the same scope
// user, so strip it from every event type here too (global scope processors run for all of them).
Sentry.getGlobalScope().addEventProcessor((event) => scrubEventUser(event));

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: ':locale?/:prefix?',
    element: <Root />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: ':prefix/',
        element: <MyAccount />,
        children: [
          // Existing (SmartSend) experience - all current routes, unchanged paths,
          // wrapped in the extracted chrome layout (pathless, adds no URL segment).
          {
            element: <SmartSendLayout />,
            children: [
              {
                path: '',
                element: <LegacyRoute r={RoutingConstants.OrderHistory} />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND, TranslationNamespaces.MYORDERS] }
              },
              {
                path: RoutingConstants.Analytics,
                element: <SmartSendDashboard />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND, TranslationNamespaces.STATEMENTS] }
              },
              {
                path: RoutingConstants.RuleManager,
                element: <RuleManager />,
                handle: {
                  namespaces: [
                    TranslationNamespaces.RULEMANAGER,
                    TranslationNamespaces.COMMON,
                    TranslationNamespaces.COUNTRY
                  ]
                }
              },
              {
                path: RoutingConstants.SmartSendSetupAbort,
                element: <AbortSetup />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND] }
              },
              {
                path: RoutingConstants.SmartSendSetup,
                element: <SmartSendOnboarding />,
                handle: {
                  namespaces: [
                    TranslationNamespaces.SMARTSEND,
                    TranslationNamespaces.COMMON,
                    TranslationNamespaces.COUNTRY
                  ]
                }
              },
              {
                path: RoutingConstants.AwaitingDispatch,
                element: <MarketplaceTracking />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND] }
              },
              {
                path: RoutingConstants.PackageContents,
                element: <PickAndPacking />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND] }
              },
              {
                path: RoutingConstants.Manifests,
                element: <Manifests />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND] }
              },
              {
                path: RoutingConstants.CustomerFeatures,
                element: <CustomerFeatures />,
                handle: { namespaces: [TranslationNamespaces.SETTINGS] }
              },
              // Areas
              {
                path: '',
                element: <OrdersLayout />,
                handle: { namespaces: [TranslationNamespaces.MYORDERS, TranslationNamespaces.SMARTSEND] },
                children: childrenOfOrders
              },
              {
                path: '',
                element: <SettingsLayout />,
                handle: {
                  namespaces: [
                    TranslationNamespaces.ACCOUNT_LINKING,
                    TranslationNamespaces.API_CLIENTS,
                    TranslationNamespaces.MYDETAILS,
                    TranslationNamespaces.SETTINGS,
                    TranslationNamespaces.SAVED_ADDRESSES,
                    TranslationNamespaces.COUNTRY,
                    TranslationNamespaces.COMMON
                  ]
                },
                children: childrenOfAccount
              },
              {
                path: '',
                element: <SupportLayout />,
                handle: {
                  namespaces: [
                    TranslationNamespaces.SELFSERVE,
                    TranslationNamespaces.SUPPORT_TICKET,
                    TranslationNamespaces.SURCHARGES,
                    TranslationNamespaces.CLAIMS,
                    TranslationNamespaces.SMARTSEND
                  ]
                },
                children: childrenOfSupport
              },
              {
                path: '',
                element: <ToolsLayout />,
                handle: { namespaces: [TranslationNamespaces.SMARTSEND] },
                children: childrenOfTools
              },
              {
                path: '',
                element: <RewardsLayout />,
                handle: { namespaces: [TranslationNamespaces.REWARDS] },
                children: childrenOfRewards
              },
              // Legacy
              {
                path: '',
                children: legacyRoutes
              }
            ]
          },
          // Consumer "My Account" view - additive, nested under the account/ segment.
          // The brief-aligned IA (My Orders / Support / Communications / PrePay & Balance /
          // Account) is defined in groups/consumer.tsx.
          {
            path: RoutingConstants.ConsumerRoot,
            element: <ConsumerLayout />,
            handle: {
              namespaces: [
                TranslationNamespaces.MYORDERS,
                TranslationNamespaces.SAVED_ADDRESSES,
                TranslationNamespaces.MYDETAILS,
                TranslationNamespaces.COMMUNICATION_PREFERENCES,
                TranslationNamespaces.PREPAY,
                TranslationNamespaces.COUNTRY
              ]
            },
            children: consumerRoutes
          }
        ]
      },
      {
        path: ':prefix/',
        element: <AppComponent />,
        children: [
          {
            path: RoutingConstants.AppPickingList,
            element: <PickingList />,
            errorElement: <ErrorBoundary />
          },
          {
            path: RoutingConstants.AppMobilePicking,
            element: <PickingApp />,
            errorElement: <ErrorBoundary />
          },
          {
            path: RoutingConstants.AppTariffCodes,
            element: <TariffLookup />,
            errorElement: <ErrorBoundary />
          }
        ]
      }
    ]
  },
  {
    path: '/ui/statics/signin-callback',
    element: <SignIn />
  },
  {
    path: 'ui/statics/myaccount-signin-callback',
    element: <SignIn />
  },
  {
    path: '/ui/statics/silent-callback',
    element: <Silent />
  },
  {
    path: 'ui/statics/myaccount-silent-callback',
    element: <Silent />
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
